import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wifi, WifiOff } from "lucide-react";
import { api } from "../lib/api";
import { lsGet, lsSet } from "../lib/safeStorage";

const CACHE_KEY = "habano.exchange.rates.v1";
const STALE_HOURS = 24;

function isStale(iso) {
  try { return (Date.now() - new Date(iso).getTime()) > STALE_HOURS * 3600 * 1000; }
  catch { return true; }
}
function fmt(r) { return r == null ? "—" : Math.round(Number(r)).toString(); }

/**
 * Slim home-screen pill — one-line, gold-on-dark, fully rounded.
 *
 *   • USD <int>  ·  EUR <int>  ·  CUP   [wifi icon]
 *   • Amber dot left if stale (>24h) or offline; on offline tap title says
 *     "Last available rate".
 *   • Tap → /exchange-rate detail page.
 */
export default function ExchangeRateStrip() {
  const [payload, setPayload] = useState(() => {
    try { return JSON.parse(lsGet(CACHE_KEY) || "null"); } catch { return null; }
  });
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  useEffect(() => {
    api.get("/cuba-exchange-rate").then(({ data }) => {
      if (data && Array.isArray(data.rates) && data.rates.length > 0) {
        setPayload(data);
        try { lsSet(CACHE_KEY, JSON.stringify(data)); } catch { /* ignore quota */ }
      }
    }).catch(() => { /* keep cache */ });
  }, []);

  if (!payload || !Array.isArray(payload.rates) || payload.rates.length === 0) {
    return null;
  }
  const byCur = Object.fromEntries(payload.rates.map((r) => [r.currency, r]));
  const usd = byCur.USD?.rate;
  const eur = byCur.EUR?.rate;
  const lastUpd = payload.rates[0]?.updatedAt;
  const stale = payload.stale || (lastUpd && isStale(lastUpd));
  const offline = !online;
  const flagged = !!(stale || offline);
  const title = offline ? "Last available rate" : (stale ? "Rate not updated recently" : "Cuba exchange rate");

  return (
    <Link
      to="/exchange-rate"
      data-testid="exchange-rate-strip"
      title={title}
      aria-label={title}
      className="inline-flex items-center gap-2 select-none flex-1 min-w-0 justify-start"
      style={{
          background: "#1D1D1D",
          color: "#E8B84B",
          fontFamily: "var(--font-sans, 'DM Sans')",
          fontSize: 13,
          padding: "6px 14px",
          border: "1px solid rgba(232,184,75,0.28)",
          borderRadius: 9999,
          height: 32,
          maxHeight: 36,
          // Allow the inner content to compress without wrapping when the
          // viewport is tight (≤ 360 px). Inner spans collapse gaps + drop
          // CUP label first via the helper below.
          overflow: "hidden",
          whiteSpace: "nowrap",
        }}
      >
        {flagged && (
          <span
            data-testid={offline ? "exch-offline-dot" : "exch-stale-dot"}
            style={{
              width: 6, height: 6, borderRadius: 9999,
              background: "#F59E0B",
              boxShadow: "0 0 0 2px rgba(245,158,11,0.18)",
            }}
            aria-hidden="true"
          />
        )}
        <span className="font-mono" data-testid="exch-pill-usd">USD {fmt(usd)}</span>
        <span style={{ opacity: 0.45 }}>·</span>
        <span className="font-mono" data-testid="exch-pill-eur">EUR {fmt(eur)}</span>
        <span style={{ opacity: 0.45 }}>·</span>
        <span style={{ letterSpacing: "0.05em" }}>CUP</span>
        {offline
          ? <WifiOff className="w-3.5 h-3.5 ml-1 text-amber-300" strokeWidth={1.6} />
          : <Wifi className="w-3.5 h-3.5 ml-1" strokeWidth={1.6} style={{ color: "rgba(232,184,75,0.55)" }} />}
      </Link>
  );
}
